"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MigrationMessageInputSchema = exports.MigrationAdminCreateInputSchema = exports.MigrationSubmitInputSchema = exports.MigrationUploadInputSchema = exports.MigrationProfileSchema = exports.MigrationItemPatchSchema = exports.MigrationItemSchema = exports.MigrationItemApplySchema = exports.MigrationVariantSchema = exports.MigrationIssueCodeSchema = exports.MigrationJobSchema = exports.MigrationEventSchema = exports.MigrationProgressSchema = exports.MigrationStatsSchema = exports.MigrationFileSchema = exports.MigrationMappingSchema = exports.MigrationFieldSchema = exports.MigrationClientStatusSchema = exports.MigrationJobStatusSchema = exports.MIGRATION_SOURCE_LABELS = exports.MigrationSourceSchema = exports.TOOL_DATA_MIGRATION = void 0;
exports.clientStatusFor = clientStatusFor;
const zod_1 = require("zod");
/**
 * Data Migration tool — bring a merchant's catalogue over from another POS.
 * Spec: docs/superpowers/specs/2026-10-11-data-migration-tool-design.md
 *
 * Tables (infrastructure/tools): migration-jobs (PK tenantId, SK jobId),
 * migration-items (PK jobId, SK itemKey), migration-profiles (PK profileId),
 * migration-corrections (PK scope, SK <iso>#<id>).
 */
exports.TOOL_DATA_MIGRATION = 'data-migration';
exports.MigrationSourceSchema = zod_1.z.enum([
    'square', 'clover', 'lightspeed_x', 'lightspeed_r', 'toast', 'shopify', 'other',
]);
exports.MIGRATION_SOURCE_LABELS = {
    square: 'Square',
    clover: 'Clover',
    lightspeed_x: 'Lightspeed Retail (X-Series)',
    lightspeed_r: 'Lightspeed Retail (R-Series)',
    toast: 'Toast',
    shopify: 'Shopify',
    other: 'Other / spreadsheet',
};
exports.MigrationJobStatusSchema = zod_1.z.enum([
    'uploading', 'submitted', 'analysing', 'mapped', 'dry_run_ready', 'approved',
    'backing_up', 'applying', 'stocking', 'applied', 'rolling_back', 'rolled_back', 'failed',
]);
/** The five steps a client sees on their status page. */
exports.MigrationClientStatusSchema = zod_1.z.enum(['received', 'analysing', 'in_review', 'importing', 'done', 'needs_input']);
function clientStatusFor(status, needsInput) {
    if (needsInput)
        return 'needs_input';
    switch (status) {
        case 'uploading':
        case 'submitted': return 'received';
        case 'analysing': return 'analysing';
        case 'mapped':
        case 'dry_run_ready':
        case 'approved':
        case 'failed':
        case 'rolling_back':
        case 'rolled_back': return 'in_review';
        case 'backing_up':
        case 'applying':
        case 'stocking': return 'importing';
        case 'applied': return 'done';
    }
}
/** Canonical fields a source column can map to. */
exports.MigrationFieldSchema = zod_1.z.enum([
    'groupKey', 'name', 'variantName', 'option1Name', 'option1Value', 'option2Name', 'option2Value',
    'option3Name', 'option3Value', 'sku', 'barcode', 'barcode2', 'price', 'cost', 'category',
    'brand', 'vendor', 'description', 'imageUrl', 'stock', 'reorderPoint', 'active', 'ignore',
]);
/**
 * A column mapping. `columns` maps source header → canonical field. Per-location stock
 * and price columns (e.g. Square "Current Quantity Main St") use `stockColumns` /
 * `priceColumns`: header → source location label.
 */
exports.MigrationMappingSchema = zod_1.z.object({
    profileId: zod_1.z.string().optional(),
    source: exports.MigrationSourceSchema,
    columns: zod_1.z.record(exports.MigrationFieldSchema),
    stockColumns: zod_1.z.record(zod_1.z.string()).default({}),
    priceColumns: zod_1.z.record(zod_1.z.string()).default({}),
    /** source location label → tenant locationId. '*' = the file's single (not per-location) stock column. */
    locationMap: zod_1.z.record(zod_1.z.string()).default({}),
    productTypeId: zod_1.z.string().optional(),
});
exports.MigrationFileSchema = zod_1.z.object({
    key: zod_1.z.string(),
    name: zod_1.z.string(),
    size: zod_1.z.number().int().nonnegative(),
    contentType: zod_1.z.string().optional(),
    uploadedAt: zod_1.z.string(),
    uploadedBy: zod_1.z.string().optional(),
});
exports.MigrationStatsSchema = zod_1.z.object({
    rows: zod_1.z.number().int().default(0),
    items: zod_1.z.number().int().default(0),
    variants: zod_1.z.number().int().default(0),
    barcodes: zod_1.z.number().int().default(0),
    toCreate: zod_1.z.number().int().default(0),
    toSkip: zod_1.z.number().int().default(0),
    withIssues: zod_1.z.number().int().default(0),
    categoriesNew: zod_1.z.number().int().default(0),
    brandsNew: zod_1.z.number().int().default(0),
    stockRows: zod_1.z.number().int().default(0),
});
exports.MigrationProgressSchema = zod_1.z.object({
    total: zod_1.z.number().int().default(0),
    done: zod_1.z.number().int().default(0),
    failed: zod_1.z.number().int().default(0),
    stockDone: zod_1.z.number().int().default(0),
});
exports.MigrationEventSchema = zod_1.z.object({
    at: zod_1.z.string(),
    by: zod_1.z.string().optional(),
    kind: zod_1.z.string(),
    message: zod_1.z.string(),
    /** Shown to the client on their status page. */
    visibleToClient: zod_1.z.boolean().optional(),
});
exports.MigrationJobSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    jobId: zod_1.z.string(),
    scope: zod_1.z.literal('all'),
    status: exports.MigrationJobStatusSchema,
    clientStatus: exports.MigrationClientStatusSchema,
    needsInput: zod_1.z.string().optional(),
    source: exports.MigrationSourceSchema.optional(),
    sourceGuess: exports.MigrationSourceSchema.optional(),
    notes: zod_1.z.string().optional(),
    files: zod_1.z.array(exports.MigrationFileSchema).default([]),
    mapping: exports.MigrationMappingSchema.optional(),
    /** Headers + first rows of the main file, for the mapping screen. */
    preview: zod_1.z.object({ headers: zod_1.z.array(zod_1.z.string()), rows: zod_1.z.array(zod_1.z.array(zod_1.z.string())) }).optional(),
    stats: exports.MigrationStatsSchema.optional(),
    progress: exports.MigrationProgressSchema.optional(),
    dryRunAt: zod_1.z.string().optional(),
    /** Later than dryRunAt ⇒ a dry run is in flight. */
    dryRunRequestedAt: zod_1.z.string().optional(),
    /** Set at approval: Ruby searches photos for picture-less products, charged to the client's Ruby Tokens. */
    rubyPhotos: zod_1.z.boolean().optional(),
    reportKey: zod_1.z.string().optional(),
    backupKey: zod_1.z.string().optional(),
    approvedBy: zod_1.z.string().optional(),
    approvedAt: zod_1.z.string().optional(),
    error: zod_1.z.string().optional(),
    events: zod_1.z.array(exports.MigrationEventSchema).default([]),
    tenantName: zod_1.z.string().optional(),
    createdBy: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
    submittedAt: zod_1.z.string().optional(),
});
exports.MigrationIssueCodeSchema = zod_1.z.enum([
    'missing_name', 'missing_price', 'bad_barcode', 'duplicate_barcode_in_file', 'duplicate_sku_in_file',
    'negative_stock', 'barcode_exists', 'sku_exists', 'unmapped_location', 'too_many_variants',
    // Publish rules (products-service-ext validate): without these the product stays a draft.
    'missing_image', 'missing_cost', 'generated_sku', 'default_category',
]);
exports.MigrationVariantSchema = zod_1.z.object({
    name: zod_1.z.string(),
    options: zod_1.z.record(zod_1.z.string()).optional(),
    sku: zod_1.z.string().optional(),
    barcodes: zod_1.z.array(zod_1.z.string()).default([]),
    /** Default retail price (applied to every mapped location without its own price). */
    price: zod_1.z.number().optional(),
    /** locationId → price, when the file has per-location prices. */
    prices: zod_1.z.record(zod_1.z.number()).optional(),
    cost: zod_1.z.number().optional(),
    /** locationId → quantity. */
    stock: zod_1.z.record(zod_1.z.number()).optional(),
    reorderPoint: zod_1.z.number().optional(),
    sourceRow: zod_1.z.number().int().optional(),
});
exports.MigrationItemApplySchema = zod_1.z.object({
    /** draft_only = created but held back from publish by a publish blocker (fix, then re-apply). */
    status: zod_1.z.enum(['pending', 'created', 'draft_only', 'published', 'stocked', 'failed', 'rolled_back']),
    productId: zod_1.z.string().optional(),
    variantIds: zod_1.z.array(zod_1.z.string()).optional(),
    categoryId: zod_1.z.string().optional(),
    brandId: zod_1.z.string().optional(),
    error: zod_1.z.string().optional(),
    at: zod_1.z.string().optional(),
});
exports.MigrationItemSchema = zod_1.z.object({
    jobId: zod_1.z.string(),
    itemKey: zod_1.z.string(),
    name: zod_1.z.string(),
    description: zod_1.z.string().optional(),
    category: zod_1.z.string().optional(),
    brand: zod_1.z.string().optional(),
    vendor: zod_1.z.string().optional(),
    imageUrls: zod_1.z.array(zod_1.z.string()).default([]),
    optionNames: zod_1.z.array(zod_1.z.string()).default([]),
    variants: zod_1.z.array(exports.MigrationVariantSchema),
    issues: zod_1.z.array(zod_1.z.object({ code: exports.MigrationIssueCodeSchema, message: zod_1.z.string() })).default([]),
    match: zod_1.z.object({ kind: zod_1.z.enum(['new', 'existing']), productId: zod_1.z.string().optional(), by: zod_1.z.enum(['barcode', 'sku']).optional() }),
    decision: zod_1.z.enum(['create', 'skip']),
    edited: zod_1.z.boolean().optional(),
    apply: exports.MigrationItemApplySchema.optional(),
    /** Publish blockers found by the dry run — the product would be created as a draft. */
    publishBlockers: zod_1.z.array(zod_1.z.string()).optional(),
    /** Outcome of the Ruby photo search, when the job opted in. */
    rubyPhoto: zod_1.z.object({ found: zod_1.z.number().int(), reason: zod_1.z.string().optional() }).optional(),
});
exports.MigrationItemPatchSchema = zod_1.z.object({
    decision: zod_1.z.enum(['create', 'skip']).optional(),
    name: zod_1.z.string().min(1).optional(),
    category: zod_1.z.string().optional(),
    brand: zod_1.z.string().optional(),
    description: zod_1.z.string().optional(),
    variants: zod_1.z.array(exports.MigrationVariantSchema).optional(),
});
exports.MigrationProfileSchema = zod_1.z.object({
    profileId: zod_1.z.string(),
    name: zod_1.z.string(),
    source: exports.MigrationSourceSchema,
    headers: zod_1.z.array(zod_1.z.string()),
    mapping: exports.MigrationMappingSchema,
    builtIn: zod_1.z.boolean().optional(),
    uses: zod_1.z.number().int().default(0),
    createdBy: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
// ── Request bodies ────────────────────────────────────────────────────────────
exports.MigrationUploadInputSchema = zod_1.z.object({
    jobId: zod_1.z.string().optional(),
    filename: zod_1.z.string().min(1).max(255),
    contentType: zod_1.z.string().optional(),
    size: zod_1.z.number().int().positive().max(2 * 1024 * 1024 * 1024),
});
exports.MigrationSubmitInputSchema = zod_1.z.object({
    source: exports.MigrationSourceSchema.optional(),
    notes: zod_1.z.string().max(4000).optional(),
});
exports.MigrationAdminCreateInputSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    source: exports.MigrationSourceSchema.optional(),
    notes: zod_1.z.string().max(4000).optional(),
});
exports.MigrationMessageInputSchema = zod_1.z.object({
    message: zod_1.z.string().min(1).max(2000),
    needsInput: zod_1.z.boolean().optional(),
});
//# sourceMappingURL=migration.schema.js.map